// packages/shared/src/services/challengeService.ts

import { doc, addDoc, updateDoc, collection, query, where, getDocs, Timestamp, getDoc, writeBatch, onSnapshot, DocumentSnapshot, QuerySnapshot } from 'firebase/firestore';
import { httpsCallable as getHttpsCallable } from 'firebase/functions';
import { FirebaseService } from './firebaseService';
import { Challenge, PlayerSlot, User, PlayerRatings } from '../types';
import { ChatService } from './chatService';


export class ChallengeService {
  private static instance: ChallengeService;
  private firebaseService: FirebaseService;

  private constructor() {
    this.firebaseService = FirebaseService.getInstance();
  }

  public static getInstance(): ChallengeService {
    if (!ChallengeService.instance) {
      ChallengeService.instance = new ChallengeService();
    }
    return ChallengeService.instance;
  }

/**
 * Create a new challenge - COMPLETE FIXED VERSION with bidirectional validation
 */
public async createChallenge(
  fromUser: User,
  toUserId: string,
  toUserName: string,
  slot: PlayerSlot,
  message: string
): Promise<Challenge> {
  const db = this.firebaseService.getFirestore();
  
  // FIRST: Check if the slot itself is already booked
  const slotRef = doc(db, 'playerSlots', slot.id);
  const slotSnap = await getDoc(slotRef);
  
  if (slotSnap.exists()) {
    const slotData = slotSnap.data();
    if (slotData.booked === true) {
      throw new Error('This time slot has already been booked');
    }
  }
  
  // SECOND: Check for any accepted challenges for this slot (from ANY user)
  const acceptedChallengeQuery = query(
    collection(db, 'challenges'),
    where('slotId', '==', slot.id),
    where('status', '==', 'accepted')
  );
  
  const acceptedChallengeSnapshot = await getDocs(acceptedChallengeQuery);
  if (!acceptedChallengeSnapshot.empty) {
    throw new Error('This time slot has already been accepted for a match');
  }
  
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  
  // THIRD: Check if user already has pending/accepted challenges for this exact slot
  // Check BOTH directions: challenges sent BY user AND challenges sent TO user
  
  // Check challenges SENT by user
  const sentChallengeQuery = query(
    collection(db, 'challenges'),
    where('fromUserId', '==', fromUser.uid),
    where('slotId', '==', slot.id),
    where('status', 'in', ['pending', 'accepted']),
    where('createdAt', '>=', sevenDaysAgo)
  );
  
  // Check challenges RECEIVED by user (for this slot owner)
  const receivedChallengeQuery = query(
    collection(db, 'challenges'),
    where('toUserId', '==', fromUser.uid),
    where('slotId', '==', slot.id),
    where('status', 'in', ['pending', 'accepted']),
    where('createdAt', '>=', sevenDaysAgo)
  );
  
  const [sentSnapshot, receivedSnapshot] = await Promise.all([
    getDocs(sentChallengeQuery),
    getDocs(receivedChallengeQuery)
  ]);
  
  // Check sent challenges
  if (!sentSnapshot.empty) {
    const existingChallenge = sentSnapshot.docs[0].data();
    console.log('Found existing sent challenge:', existingChallenge);
    
    if (existingChallenge.status === 'pending') {
      throw new Error('You already have a pending challenge for this time slot');
    } else if (existingChallenge.status === 'accepted') {
      throw new Error('This time slot has already been accepted for a match');
    }
  }
  
  // Check received challenges
  if (!receivedSnapshot.empty) {
    const existingChallenge = receivedSnapshot.docs[0].data();
    console.log('Found existing received challenge:', existingChallenge);
    
    if (existingChallenge.status === 'pending') {
      throw new Error('You already have a pending challenge for this time slot');
    } else if (existingChallenge.status === 'accepted') {
      throw new Error('This time slot has already been accepted for a match');
    }
  }
  
  // FOURTH: Check for overlapping time slots
  // Check BOTH directions: challenges sent AND received
  const slotStartTime = slot.startTime.getTime();
  const slotEndTime = slotStartTime + (slot.duration * 60 * 1000);
  
  // Get all pending and accepted challenges FROM this user
  const userSentChallengesQuery = query(
    collection(db, 'challenges'),
    where('fromUserId', '==', fromUser.uid),
    where('status', 'in', ['pending', 'accepted']),
    where('createdAt', '>=', sevenDaysAgo)
  );
  
  // Get all pending and accepted challenges TO this user
  const userReceivedChallengesQuery = query(
    collection(db, 'challenges'),
    where('toUserId', '==', fromUser.uid),
    where('status', 'in', ['pending', 'accepted']),
    where('createdAt', '>=', sevenDaysAgo)
  );
  
  const [sentChallengesSnapshot, receivedChallengesSnapshot] = await Promise.all([
    getDocs(userSentChallengesQuery),
    getDocs(userReceivedChallengesQuery)
  ]);
  
  // Combine all challenges (sent + received) for overlap checking
  const allUserChallenges = [
    ...sentChallengesSnapshot.docs,
    ...receivedChallengesSnapshot.docs
  ];
  
  // Check for overlaps
  for (const challengeDoc of allUserChallenges) {
    const challengeData = challengeDoc.data();
    const challengeStartTime = challengeData.startTime.toDate().getTime();
    const challengeEndTime = challengeData.endTime.toDate().getTime();
    
    // Check if the new slot overlaps with existing challenge
    if (slotStartTime < challengeEndTime && slotEndTime > challengeStartTime) {
      if (challengeData.status === 'pending') {
        throw new Error('You already have a pending challenge that overlaps with this time slot');
      } else if (challengeData.status === 'accepted') {
        throw new Error('You already have an accepted match that overlaps with this time slot');
      }
    }
  }
  
  // Calculate end time
  const endTime = new Date(slot.startTime.getTime() + slot.duration * 60000);
  
  const challengeData: Omit<Challenge, 'id'> = {
    fromUserId: fromUser.uid,
    fromUserName: fromUser.name,
    toUserId,
    toUserName,
    slotId: slot.id,
    startTime: slot.startTime,
    endTime,
    courtId: slot.courtId,
    courtName: slot.court,
    toPlayerRatings:  slot.playerRatings || { utr: null, ntrp: null },
    fromPlayerRatings: fromUser.preferences?.ratings || { utr: null, ntrp: null },
    message,
    status: 'pending',
    createdAt: new Date(),
    updatedAt: new Date()
  };
  
  const docRef = await addDoc(collection(db, 'challenges'), challengeData);
  return { id: docRef.id, ...challengeData };
}

  /**
   * Update challenge status
   */
  public async updateChallengeStatus(
    challengeId: string, 
    status: Challenge['status']
  ): Promise<void> {
    const db = this.firebaseService.getFirestore();
    await updateDoc(doc(db, 'challenges', challengeId), {
      status,
      updatedAt: new Date()
    });
  }

  /**
   * Get challenges sent by a user
   */
  public async getSentChallenges(userId: string): Promise<Challenge[]> {
    const db = this.firebaseService.getFirestore();
    const q = query(
      collection(db, 'challenges'),
      where('fromUserId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startTime: doc.data().startTime.toDate(),
      endTime: doc.data().endTime.toDate(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate()
    })) as Challenge[];
  }

  /**
   * Get challenges received by a user
   */
  public async getReceivedChallenges(userId: string): Promise<Challenge[]> {
    const db = this.firebaseService.getFirestore();
    const q = query(
      collection(db, 'challenges'),
      where('toUserId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startTime: doc.data().startTime.toDate(),
      endTime: doc.data().endTime.toDate(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate()
    })) as Challenge[];
  }

  /**
   * Accept a challenge - Try direct Firestore first, then callable function as fallback
   */
  public async acceptChallenge(challengeId: string): Promise<void> {
    try {
      // Try direct Firestore operations first (more reliable with emulator)
      await this.acceptChallengeDirectly(challengeId);
      console.log('Challenge accepted successfully via direct Firestore operations');
    } catch (directError) {
      console.error('Error accepting challenge via direct operations:', directError);
      
      // Fallback to callable function
      try {
        const functions = this.firebaseService.getFunctions();
        const acceptChallengeFunction = getHttpsCallable(functions, 'acceptChallenge');
        
        const result = await acceptChallengeFunction({ challengeId });
        console.log('Challenge accepted successfully via cloud function:', result.data);
      } catch (callableError) {
        console.error('Error accepting challenge via callable function:', callableError);
        throw new Error('Failed to accept challenge. Please try again.');
      }
    }
  }

  /**
 * Direct Firestore implementation - FIXED VERSION with atomic slot booking check AND CHAT CREATION
 */
private async acceptChallengeDirectly(challengeId: string): Promise<void> {
  const db = this.firebaseService.getFirestore();
  
  // Get the challenge first
  const challengeDocRef = doc(db, 'challenges', challengeId);
  const challengeDocSnap = await getDoc(challengeDocRef);
  
  if (!challengeDocSnap.exists()) {
    throw new Error('Challenge not found');
  }
  
  const challengeData = challengeDocSnap.data();
  const challenge = {
    ...challengeData,
    startTime: challengeData.startTime.toDate(),
    endTime: challengeData.endTime.toDate(),
    createdAt: challengeData.createdAt.toDate(),
    updatedAt: challengeData.updatedAt.toDate()
  } as Challenge;

  // Verify the challenge data has required fields
  if (!challenge.fromUserId || !challenge.toUserId || !challenge.slotId) {
    throw new Error('Invalid challenge data - missing required fields');
  }
  
  // CRITICAL FIX: Check if challenge is still pending
  if (challenge.status !== 'pending') {
    throw new Error('Challenge is no longer pending');
  }
  
  console.log('Accepting challenge:', challengeId, 'for slot:', challenge.slotId);
  
  try {
    // CRITICAL FIX: Check if slot is already booked BEFORE accepting
    const slotRef = doc(db, 'playerSlots', challenge.slotId);
    const slotSnap = await getDoc(slotRef);
    
    if (!slotSnap.exists()) {
      throw new Error('Slot not found');
    }
    
    const slotData = slotSnap.data();
    if (slotData.booked === true) {
      throw new Error('This time slot has already been booked by another player');
    }
    
    // CRITICAL FIX: Use a batch operation to make slot booking, challenge acceptance, and chat creation atomic
    const batch = writeBatch(db);
    
    // Step 1: Accept the challenge
    batch.update(challengeDocRef, {
      status: 'accepted',
      updatedAt: new Date()
    });
    
    // Step 2: Mark the slot as booked (in same transaction)
    batch.update(slotRef, {
      booked: true,
      bookedAt: new Date(),
      bookedBy: challenge.fromUserId,
      challengeId: challengeId
    });
    
    // Step 3: Create chat document for the accepted challenge
    const expiresAt = new Date(challenge.startTime.getTime() + 48 * 60 * 60 * 1000);
    const chatData = {
      challengeId: challengeId,
      participants: [challenge.fromUserId, challenge.toUserId],
      participantNames: [challenge.fromUserName, challenge.toUserName],
      matchTime: challenge.startTime,
      courtName: challenge.courtName,
      createdAt: new Date(),
      expiresAt: expiresAt,
      messageCount: 0
    };
    
    // Use challengeId as the chat document ID for easy lookup
    const chatRef = doc(db, 'chats', challengeId);
    batch.set(chatRef, chatData);
    
    // Execute all operations atomically
    await batch.commit();
    console.log('Challenge accepted, slot booked, and chat created atomically');
    
    // Step 4: Find and withdraw other challenges (after atomic booking)
    await this.withdrawOtherChallengesForSlot(challenge.slotId, challengeId, challenge);
    
  } catch (error) {
    console.error('Error in acceptChallengeDirectly:', error);
    throw new Error('Failed to accept challenge. Please try again.');
  }
}

  /**
   * Withdraw other challenges for a slot using multiple targeted queries
   */
  private async withdrawOtherChallengesForSlot(slotId: string, acceptedChallengeId: string, challenge: Challenge): Promise<void> {
    const db = this.firebaseService.getFirestore();
    
    try {
      console.log(`Looking for other challenges to withdraw for slot: ${slotId}`);
      
      // Method 1: Try to get all pending challenges and filter client-side
      try {
        await this.withdrawUsingPendingQuery(slotId, acceptedChallengeId);
      } catch (pendingQueryError) {
        console.error('Pending query method failed, trying alternative:', pendingQueryError);
        
        // Method 2: Get challenges by user and filter
        await this.withdrawOtherChallengesAlternative(slotId, acceptedChallengeId, challenge);
      }
      
    } catch (error) {
      console.error('Error in withdrawOtherChallengesForSlot (non-critical):', error);
      // Don't throw - the main challenge acceptance already succeeded
    }
  }

  /**
   * Withdraw challenges using pending status query
   */
  private async withdrawUsingPendingQuery(slotId: string, acceptedChallengeId: string): Promise<void> {
    const db = this.firebaseService.getFirestore();
    
    console.log('Trying pending challenges query method...');
    
    // Get all pending challenges (we'll filter client-side)
    const allPendingQuery = query(
      collection(db, 'challenges'),
      where('status', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(allPendingQuery);
    console.log(`Found ${querySnapshot.docs.length} total pending challenges`);
    
    // Filter for challenges matching our slot ID (client-side filtering)
    const challengesToWithdraw = querySnapshot.docs.filter(doc => {
      const data = doc.data();
      return data.slotId === slotId && doc.id !== acceptedChallengeId;
    });
    
    console.log(`Found ${challengesToWithdraw.length} challenges to withdraw for slot ${slotId}`);
    
    if (challengesToWithdraw.length === 0) {
      console.log('No other challenges to withdraw');
      return;
    }
    
    // Withdraw challenges individually to avoid batch issues
    const withdrawPromises = challengesToWithdraw.map(async (doc) => {
      try {
        await updateDoc(doc.ref, {
          status: 'withdrawn',
          updatedAt: new Date()
        });
        console.log(`Successfully withdrew challenge ${doc.id}`);
        return { success: true, id: doc.id };
      } catch (error) {
        console.error(`Failed to withdraw challenge ${doc.id}:`, error);
        return { success: false, id: doc.id, error };
      }
    });
    
    const results = await Promise.allSettled(withdrawPromises);
    const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
    
    console.log(`Withdrawal complete: ${successful}/${challengesToWithdraw.length} challenges withdrawn successfully`);
  }

  /**
   * Alternative method: Get challenges by user and filter (if the above still fails)
   */
  private async withdrawOtherChallengesAlternative(slotId: string, acceptedChallengeId: string, challenge: Challenge): Promise<void> {
    const db = this.firebaseService.getFirestore();
    
    try {
      console.log('Trying alternative withdrawal method...');
      
      // Get challenges sent TO the slot owner
      const receivedChallengesQuery = query(
        collection(db, 'challenges'),
        where('toUserId', '==', challenge.toUserId),
        where('status', '==', 'pending')
      );
      
      const receivedSnapshot = await getDocs(receivedChallengesQuery);
      console.log(`Found ${receivedSnapshot.docs.length} pending challenges for user ${challenge.toUserId}`);
      
      // Filter for our specific slot
      const challengesToWithdraw = receivedSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.slotId === slotId && doc.id !== acceptedChallengeId;
      });
      
      console.log(`Alternative method: found ${challengesToWithdraw.length} challenges to withdraw`);
      
      // Withdraw them
      for (const doc of challengesToWithdraw) {
        try {
          await updateDoc(doc.ref, {
            status: 'withdrawn',
            updatedAt: new Date()
          });
          console.log(`Alternative method: withdrew challenge ${doc.id}`);
        } catch (error) {
          console.error(`Alternative method: failed to withdraw challenge ${doc.id}:`, error);
        }
      }
      
    } catch (error) {
      console.error('Alternative withdrawal method also failed:', error);
    }
  }

  /**
   * Reject a challenge
   */
  public async rejectChallenge(challengeId: string): Promise<void> {
    await this.updateChallengeStatus(challengeId, 'rejected');
  }

  /**
   * Withdraw a challenge
   */
  public async withdrawChallenge(challengeId: string): Promise<void> {
    await this.updateChallengeStatus(challengeId, 'withdrawn');
  }

  /**
   * Subscribe to received challenges
   */
  public subscribeToReceivedChallenges(
    userId: string, 
    callback: (challenges: Challenge[]) => void
  ): () => void {
    const db = this.firebaseService.getFirestore();
    const q = query(
      collection(db, 'challenges'),
      where('toUserId', '==', userId)
    );
    
    return onSnapshot(q, (snapshot: QuerySnapshot) => {
      const challenges = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        startTime: doc.data().startTime.toDate(),
        endTime: doc.data().endTime.toDate(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate()
      })) as Challenge[];
      
      callback(challenges);
    });
  }

/**
 * Cancel an accepted challenge (can be done by either party)
 * This will:
 * - Change challenge status to 'cancelled'
 * - Send cancellation message to chat
 * - Unbook the slot
 * - Trigger email notification via Firebase function
 */
public async cancelAcceptedChallenge(
  challengeId: string,
  cancelledByUserId: string,
  cancellationMessage: string
): Promise<void> {
  const db = this.firebaseService.getFirestore();
  
  try {
    // Get the challenge
    const challengeRef = doc(db, 'challenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);
    
    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }
    
    const challenge = { id: challengeSnap.id, ...challengeSnap.data() } as Challenge;
    
    // Verify challenge is accepted
    if (challenge.status !== 'accepted') {
      throw new Error('Only accepted challenges can be cancelled');
    }
    
    // Verify the user is a participant
    if (challenge.fromUserId !== cancelledByUserId && challenge.toUserId !== cancelledByUserId) {
      throw new Error('You are not a participant in this challenge');
    }
    
    // Get the chat service to send cancellation message
    const chatService = ChatService.getInstance();
    
    // Determine who is cancelling
    const cancellerName = challenge.fromUserId === cancelledByUserId 
      ? challenge.fromUserName 
      : challenge.toUserName;
    
    // Use a batch for atomic operations
    const batch = writeBatch(db);
    
    // Update challenge status to 'cancelled'
    batch.update(challengeRef, {
      status: 'cancelled',
      cancelledBy: cancelledByUserId,
      cancelledAt: new Date(),
      cancellationMessage: cancellationMessage,
      updatedAt: new Date()
    });
    
    // Unbook the slot
    const slotRef = doc(db, 'playerSlots', challenge.slotId);
    batch.update(slotRef, {
      booked: false,
      bookedAt: null,
      bookedBy: null,
      challengeId: null
    });
    
    // Commit the batch
    await batch.commit();
    
    // Send message to chat (if chat exists and is still active)
    try {
      const chat = await chatService.getChatById(challengeId);
      if (chat && chatService.isChatActive(chat)) {
        await chatService.sendMessage(
          challengeId,
          cancelledByUserId,
          cancellerName,
          cancellationMessage
        );
      }
    } catch (chatError) {
      console.error('Error sending cancellation message to chat:', chatError);
      // Don't fail the whole operation if chat message fails
    }
    
    console.log(`Challenge ${challengeId} cancelled by ${cancelledByUserId}`);
    
  } catch (error) {
    console.error('Error cancelling challenge:', error);
    throw error;
  }
}

/**
 * Update player ratings for relevant user challenges
 * Updates pending challenges from today onwards, using user's timezone
 */
public async updateUserChallengesRatings(
  userId: string, 
  newRatings: PlayerRatings,
  fallbackTimezone?: string
): Promise<void> {
  const db = this.firebaseService.getFirestore();
  
  // Get current time for comparison
  const now = new Date();
  
  // Query 1: Pending challenges sent BY this user (update fromPlayerRatings)
  const sentChallengesQuery = query(
    collection(db, 'challenges'),
    where('fromUserId', '==', userId),
    where('status', '==', 'pending')
  );
  
  // Query 2: Pending challenges received BY this user (update toPlayerRatings)
  const receivedChallengesQuery = query(
    collection(db, 'challenges'),
    where('toUserId', '==', userId),
    where('status', '==', 'pending')
  );
  
  const [sentSnapshot, receivedSnapshot] = await Promise.all([
    getDocs(sentChallengesQuery),
    getDocs(receivedChallengesQuery)
  ]);
  
  if (sentSnapshot.empty && receivedSnapshot.empty) {
    console.log('No pending challenges found for user, nothing to update');
    return;
  }
  
  const batch = writeBatch(db);
  let updatedCount = 0;
  
  // Process challenges sent BY this user (update fromPlayerRatings)
  sentSnapshot.docs.forEach(doc => {
    const data = doc.data();
    const challengeStartTime = data.startTime?.toDate ? data.startTime.toDate() : new Date(data.startTime);
    const challengeTimezone = data.timezone || fallbackTimezone || 'UTC';
    
    try {
      // Calculate start of today in the challenge's timezone
      const todayInChallengeTZ = new Date(now.toLocaleString("en-US", {timeZone: challengeTimezone}));
      todayInChallengeTZ.setHours(0, 0, 0, 0);
      
      // Convert back to UTC for comparison
      const offsetDiff = now.getTime() - new Date(now.toLocaleString("en-US", {timeZone: challengeTimezone})).getTime();
      const cutoffTimeUTC = new Date(todayInChallengeTZ.getTime() - offsetDiff);
      
      // Update if challenge is from today onwards
      if (challengeStartTime >= cutoffTimeUTC) {
        batch.update(doc.ref, {
          fromPlayerRatings: newRatings,
          updatedAt: new Date()
        });
        updatedCount++;
      }
    } catch (error) {
      console.warn(`Invalid timezone ${challengeTimezone} for challenge ${doc.id}, falling back to UTC`);
      
      // Fallback: use UTC comparison
      const todayUTC = new Date();
      todayUTC.setUTCHours(0, 0, 0, 0);
      
      if (challengeStartTime >= todayUTC) {
        batch.update(doc.ref, {
          fromPlayerRatings: newRatings,
          updatedAt: new Date()
        });
        updatedCount++;
      }
    }
  });
  
  // Process challenges received BY this user (update toPlayerRatings)
  receivedSnapshot.docs.forEach(doc => {
    const data = doc.data();
    const challengeStartTime = data.startTime?.toDate ? data.startTime.toDate() : new Date(data.startTime);
    const challengeTimezone = data.timezone || fallbackTimezone || 'UTC';
    
    try {
      // Calculate start of today in the challenge's timezone
      const todayInChallengeTZ = new Date(now.toLocaleString("en-US", {timeZone: challengeTimezone}));
      todayInChallengeTZ.setHours(0, 0, 0, 0);
      
      // Convert back to UTC for comparison
      const offsetDiff = now.getTime() - new Date(now.toLocaleString("en-US", {timeZone: challengeTimezone})).getTime();
      const cutoffTimeUTC = new Date(todayInChallengeTZ.getTime() - offsetDiff);
      
      // Update if challenge is from today onwards
      if (challengeStartTime >= cutoffTimeUTC) {
        batch.update(doc.ref, {
          toPlayerRatings: newRatings,
          updatedAt: new Date()
        });
        updatedCount++;
      }
    } catch (error) {
      console.warn(`Invalid timezone ${challengeTimezone} for challenge ${doc.id}, falling back to UTC`);
      
      // Fallback: use UTC comparison
      const todayUTC = new Date();
      todayUTC.setUTCHours(0, 0, 0, 0);
      
      if (challengeStartTime >= todayUTC) {
        batch.update(doc.ref, {
          toPlayerRatings: newRatings,
          updatedAt: new Date()
        });
        updatedCount++;
      }
    }
  });
  
  if (updatedCount > 0) {
    await batch.commit();
    console.log(`Updated ratings for ${updatedCount} current/future pending challenges`);
  } else {
    console.log('No current/future pending challenges found to update');
  }
}


  /**
   * Clean up stale challenges for debugging - optional utility method
   */
  public async cleanupStaleChallenge(userId: string, slotId: string): Promise<void> {
    const db = this.firebaseService.getFirestore();
    
    console.log(`Cleaning up stale challenges for user ${userId} and slot ${slotId}`);
    
    // Find all challenges for this user and slot
    const staleQuery = query(
      collection(db, 'challenges'),
      where('fromUserId', '==', userId),
      where('slotId', '==', slotId)
    );
    
    const staleSnapshot = await getDocs(staleQuery);
    
    console.log(`Found ${staleSnapshot.docs.length} challenges for this slot`);
    
    for (const doc of staleSnapshot.docs) {
      const data = doc.data();
      console.log('Challenge status:', data.status, 'ID:', doc.id);
      
      // If the challenge is not pending or accepted, but still showing up,
      // this indicates a data inconsistency
      if (data.status !== 'pending' && data.status !== 'accepted') {
        console.log('Found stale challenge that should be filtered out:', doc.id, data.status);
      }
    }
  }
}

export default ChallengeService;